// ./src/utils/api/LostReportService.ts
//
// ROADMAP #68 — lost & found: someone says they lost something at an event (on the event site, or taken by the crew at
// the desk / by phone). The crew match a report to an item, close it, or reopen it; `returned` is set by the item's
// handover. OpResult-native, event-scoped; owner or crew with lostfound.manage.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";
import { LostFoundContactInput, LostFoundPerson } from "./LostFoundItemService";

export const LOST_REPORT_STATE = {
  OPEN: "open",
  MATCHED: "matched",
  RETURNED: "returned",
  CLOSED: "closed",
} as const;

export type LostReportState = (typeof LOST_REPORT_STATE)[keyof typeof LOST_REPORT_STATE];

/** The reasons the crew may close a report with (`expired` is the keep-until sweep's). */
export const LOST_REPORT_CLOSE_REASONS = ["not_found", "withdrawn"] as const;

const REPORT_FIELDS = `
  id
  accountId
  eventId
  eventTimeSlotId
  sequenceNo
  source
  isTest
  eventGuestId
  personId
  reporter { kind id name email phone removed }
  category
  description
  lostWhere
  lostAt
  latitude
  longitude
  locationAccuracyM
  reportState
  lostFoundItemId
  matchedItem { id sequenceNo category itemState publicDescription }
  closedReason
  keepUntil
  rights
  createdAt
  updatedAt
`;

const RESULT = (name: string) => `
    ${name} {
      code
      data { ${REPORT_FIELDS} }
      errors { name errors warnings }
    }`;

const LIST_QUERY = gql`
  query LostReportList($filter: LostReportFilter, $params: PaginationAndSorting) {
    lostReportList(filter: $filter, params: $params) {
      code
      total
      data { ${REPORT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const GET_QUERY = gql`
  query LostReportGet($id: ID) {${RESULT("lostReportGet(id: $id)")}
  }
`;

const CREATE_MUTATION = gql`
  mutation LostReportCreate($params: LostReportInput) {${RESULT("lostReportCreate(params: $params)")}
  }
`;

const UPDATE_MUTATION = gql`
  mutation LostReportUpdate($where: LostReportWhereInput, $params: LostReportInput) {${RESULT("lostReportUpdate(where: $where, params: $params)")}
  }
`;

const REMOVE_MUTATION = gql`
  mutation LostReportRemove($where: LostReportWhereInput) {
    lostReportRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

const MOVE_MUTATION = gql`
  mutation LostReportMove($params: LostReportMoveInput) {${RESULT("lostReportMove(params: $params)")}
  }
`;

export interface LostReportMatchedItem {
  id?: string | null;
  sequenceNo?: number | null;
  category?: string | null;
  itemState?: string | null;
  publicDescription?: string | null;
}

export interface LostReport {
  id?: string;
  accountId?: string;
  eventId?: string;
  eventTimeSlotId?: string | null;
  sequenceNo?: number | null;
  source?: string;
  /** Made while the event was in test mode — hidden once it goes live. */
  isTest?: boolean | null;
  eventGuestId?: string | null;
  personId?: string | null;
  reporter?: LostFoundPerson | null;
  category?: string;
  description?: string | null;
  lostWhere?: string | null;
  lostAt?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  locationAccuracyM?: number | null;
  reportState?: LostReportState | string;
  lostFoundItemId?: string | null;
  matchedItem?: LostReportMatchedItem | null;
  closedReason?: string | null;
  keepUntil?: string | null;
  rights?: string[];
  createdAt?: string;
  updatedAt?: string;
}

export interface LostReportInput {
  /** Create-only. */
  eventId?: string;
  /** Create-only: the reporter as a guest — or reporterContact. */
  eventGuestId?: string | null;
  reporterContact?: LostFoundContactInput | null;
  category?: string;
  description?: string | null;
  lostWhere?: string | null;
  lostAt?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  locationAccuracyM?: number | null;
  eventTimeSlotId?: string | null;
}

export interface LostReportFilter {
  id?: string[];
  eventId?: string[];
  eventTimeSlotId?: string[];
  reportState?: string[];
  category?: string[];
  source?: string[];
  eventGuestId?: string[];
  lostFoundItemId?: string[];
  searchKeyword?: string;
}

export interface LostReportMoveInput {
  id: string;
  toState: "open" | "matched" | "closed";
  lostFoundItemId?: string | null;
  closedReason?: string | null;
}

export class LostReportService {
  static async list(filter?: LostReportFilter, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(GET_QUERY, { id });
  }

  static async create(params: LostReportInput): Promise<OpResult> {
    return ApiService.executeMutation(CREATE_MUTATION, { params });
  }

  static async update(id: string, params: LostReportInput): Promise<OpResult> {
    return ApiService.executeMutation(UPDATE_MUTATION, { where: { id }, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(REMOVE_MUTATION, { where: { id } });
  }

  static async move(params: LostReportMoveInput): Promise<OpResult> {
    return ApiService.executeMutation(MOVE_MUTATION, { params });
  }
}
